import React, { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useForm, SubmitHandler } from 'react-hook-form';
import TermsModal from '../components/modal/TermsModal';
import logo from '../assets/logo.svg';
import semi from '../assets/semi.svg';
import '../styles/RegisterPage.scss';

interface IFormData {
  userId: string;
  password: string;
  confirmPassword: string;
  fullName: string;
  email: string;
  age: string;
  gender: string;
  phonePart1: string;
  phonePart2: string;
  phonePart3: string;
  referredBy: string;
  agreeToTerms: boolean;
  agreeToPrivacyPolicy: boolean;
  agreeToLocation: boolean;
  agreeToSensitiveInfo: boolean;
}

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [agreements, setAgreements] = useState({
    agreeToAll: false,
    agreeToTerms: false,
    agreeToPrivacyPolicy: false,
    agreeToLocation: false,
    agreeToSensitiveInfo: false,
  });

  const [modal, setModal] = useState({ isOpen: false, title: '', content: '' });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    trigger,
    watch,
  } = useForm<IFormData>({
    defaultValues: {
      userId: '',
      fullName: '',
      password: '',
      confirmPassword: '',
      email: '',
      age: '',
      gender: 'male',
      phonePart1: '010',
      phonePart2: '',
      phonePart3: '',
      referredBy: '',
    },
    mode: 'onChange',
  });

  const phonePart1Ref = useRef<HTMLInputElement>(null);
  const phonePart2Ref = useRef<HTMLInputElement>(null);
  const phonePart3Ref = useRef<HTMLInputElement>(null);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let filteredValue = value.replace(/[^0-9]/g, '');
    if (name === 'phonePart1') {
      filteredValue = filteredValue.slice(0, 3);
    } else if (name === 'phonePart2' || name === 'phonePart3') {
      filteredValue = filteredValue.slice(0, 4);
    }

    setValue(name as keyof IFormData, filteredValue);
    trigger(name as keyof IFormData);

    if (name === 'phonePart1' && filteredValue.length === 3) {
      phonePart2Ref.current?.focus();
    } else if (name === 'phonePart2' && filteredValue.length === 4) {
      phonePart3Ref.current?.focus();
    }
  };

  const handlePhoneKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const { name, value, selectionStart } = e.currentTarget;

    if (e.key === 'Backspace' && selectionStart === 0 && value === '') {
      e.preventDefault();

      if (name === 'phonePart3') {
        phonePart2Ref.current?.focus();
      } else if (name === 'phonePart2') {
        phonePart1Ref.current?.focus();
      }
    }
  };

  const handleAgreementChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    if (name === 'agreeToAll') {
      setAgreements({
        agreeToAll: checked,
        agreeToTerms: checked,
        agreeToPrivacyPolicy: checked,
        agreeToLocation: checked,
        agreeToSensitiveInfo: checked,
      });
    } else {
      const newAgreements = { ...agreements, [name]: checked };
      const allChecked =
        newAgreements.agreeToTerms &&
        newAgreements.agreeToPrivacyPolicy &&
        newAgreements.agreeToLocation &&
        newAgreements.agreeToSensitiveInfo;
      setAgreements({ ...newAgreements, agreeToAll: allChecked });
    }
  };

  const allAgreed =
    agreements.agreeToTerms &&
    agreements.agreeToPrivacyPolicy &&
    agreements.agreeToLocation &&
    agreements.agreeToSensitiveInfo;

  const showTerms = async (type: string) => {
    try {
      const response = await axios.get('/api/terms');
      const terms = response.data[type];
      const contentWithBreaks = terms.content.replace(/\n/g, '<br />');
      setModal({ isOpen: true, title: terms.title, content: contentWithBreaks });
    } catch (error) {
      console.error('Failed to fetch terms', error);
    }
  };

  const onSubmit: SubmitHandler<IFormData> = async data => {
    if (!allAgreed) {
      alert('모든 약관에 동의해야 회원가입을 진행할 수 있습니다.');
      return;
    }
    try {
      const fullPhoneNumber = `${data.phonePart1}-${data.phonePart2}-${data.phonePart3}`;
      const registrationData = {
        ...data,
        phoneNumber: fullPhoneNumber,
        agreeToTerms: agreements.agreeToTerms,
        agreeToPrivacyPolicy: agreements.agreeToPrivacyPolicy,
        agreeToLocation: agreements.agreeToLocation,
        agreeToSensitiveInfo: agreements.agreeToSensitiveInfo,
      };
      await axios.post('/api/users/register', registrationData);
      alert('회원가입이 완료되었습니다. 로그인 페이지로 이동합니다.');
      navigate('/login');
    } catch (error: any) {
      console.error('Registration failed', error);
      const errorMessage = error.response?.data || '회원가입에 실패했습니다. 다시 시도해 주세요.';
      alert(errorMessage);
    }
  };

  return (
    <div className="register-page">
      <div className="register-container">
        <img src={logo} className="logo" alt="RE:BORN TALK" />
        <p className="subtitle">
          당신의 마음에 다시
          <img src={semi} alt="" /> 생기를 불어넣다
        </p>

        <div className="section-title">
          <span>회원가입</span>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="form-group">
            <div className="input-wrapper">
              <label htmlFor="userId">아이디</label>
              <input
                type="text"
                id="userId"
                placeholder="아이디를 입력해 주세요"
                {...register('userId', {
                  required: '아이디는 필수 항목입니다.',
                  minLength: { value: 5, message: '아이디는 5글자 이상이어야 합니다.' },
                })}
              />
            </div>
            {errors.userId && <p className="error-message">{errors.userId.message}</p>}
          </div>

          <div className="form-group">
            <div className="input-wrapper">
              <label htmlFor="password">비밀번호</label>
              <input
                type="password"
                id="password"
                placeholder="비밀번호를 입력해 주세요"
                {...register('password', {
                  validate: value => {
                    if (!value) return '비밀번호는 필수 항목입니다.';
                    if (value.length < 8) return '비밀번호는 8글자 이상이어야 합니다.';
                    return true;
                  },
                })}
              />
            </div>
            {errors.password && <p className="error-message">{errors.password.message}</p>}
          </div>

          <div className="form-group">
            <div className="input-wrapper">
              <label htmlFor="confirmPassword">비밀번호 확인</label>
              <input
                type="password"
                id="confirmPassword"
                placeholder="비밀번호를 다시 입력해 주세요"
                {...register('confirmPassword', {
                  validate: value => {
                    if (!value) return '비밀번호 확인은 필수 항목입니다.';
                    if (value !== watch('password')) return '비밀번호가 일치하지 않습니다.';
                    return true;
                  },
                })}
              />
            </div>
            {errors.confirmPassword && (
              <p className="error-message">{errors.confirmPassword.message}</p>
            )}
          </div>

          <div className="form-group">
            <div className="input-wrapper">
              <label htmlFor="email">이메일</label>
              <input
                type="email"
                id="email"
                placeholder="이메일을 입력해 주세요"
                {...register('email', {
                  validate: value => {
                    if (!value) return '이메일은 필수 항목입니다.';
                    if (!/\S+@\S+\.\S+/.test(value)) return '유효한 이메일 주소를 입력해주세요.';
                    return true;
                  },
                })}
              />
            </div>
            {errors.email && <p className="error-message">{errors.email.message}</p>}
          </div>

          <div className="form-group">
            <div className="input-wrapper">
              <label htmlFor="fullName">이름</label>
              <input
                type="text"
                id="fullName"
                placeholder="이름을 입력해 주세요"
                {...register('fullName', { required: '이름은 필수 항목입니다.' })}
              />
            </div>
            {errors.fullName && <p className="error-message">{errors.fullName.message}</p>}
          </div>

          <div className="form-group">
            <div className="input-wrapper">
              <label htmlFor="age">나이</label>
              <input
                type="number"
                id="age"
                placeholder="나이를 입력해 주세요"
                {...register('age', {
                  validate: value => {
                    const age = Number(value);
                    if (!value) return '나이는 필수 항목입니다.';
                    if (age < 1 || age > 120) return '유효한 나이를 입력해주세요 (1-120)';
                    return true;
                  },
                })}
              />
            </div>
            {errors.age && <p className="error-message">{errors.age.message}</p>}
          </div>

          <div className="form-group">
            <div className="input-wrapper">
              <label htmlFor="gender">성별</label>
              <select id="gender" {...register('gender', { required: '성별은 필수 항목입니다.' })}>
                <option value="male">남</option>
                <option value="female">여</option>
              </select>
            </div>
            {errors.gender && <p className="error-message">{errors.gender.message}</p>}
          </div>

          <div className="form-group">
            <div className="input-wrapper">
              <label htmlFor="phoneNumber">휴대폰 번호</label>
              <div className="phone-input-group">
                <input
                  type="text"
                  id="phonePart1"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={3}
                  {...register('phonePart1', {
                    required: '휴대폰 번호는 필수 항목입니다.',
                    maxLength: 3,
                    minLength: 3,
                  })}
                  onChange={handlePhoneChange}
                  onKeyDown={handlePhoneKeyDown}
                  ref={phonePart1Ref}
                />
                <span>-</span>
                <input
                  type="text"
                  id="phonePart2"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={4}
                  {...register('phonePart2', {
                    required: '휴대폰 번호는 필수 항목입니다.',
                    maxLength: 4,
                    minLength: 4,
                  })}
                  onChange={handlePhoneChange}
                  onKeyDown={handlePhoneKeyDown}
                  ref={phonePart2Ref}
                />
                <span>-</span>
                <input
                  type="text"
                  id="phonePart3"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={4}
                  {...register('phonePart3', {
                    required: '휴대폰 번호는 필수 항목입니다.',
                    maxLength: 4,
                    minLength: 4,
                  })}
                  onChange={handlePhoneChange}
                  onKeyDown={handlePhoneKeyDown}
                  ref={phonePart3Ref}
                />
              </div>
            </div>
            {(errors.phonePart1 || errors.phonePart2 || errors.phonePart3) && (
              <p className="error-message">휴대폰 번호는 필수 항목입니다</p>
            )}
          </div>

          <div className="form-group">
            <div className="input-wrapper">
              <label htmlFor="referredBy">추천인 코드</label>
              <input
                type="text"
                id="referredBy"
                placeholder="추천인 코드를 입력해주세요"
                {...register('referredBy')}
              />
            </div>
          </div>

          <div className="section-title">
            <span>이용약관 동의</span>
          </div>

          <div className="form-group agreements">
            <div className="agreement-item all-agreement">
              <input
                type="checkbox"
                id="agreeToAll"
                name="agreeToAll"
                checked={agreements.agreeToAll}
                onChange={handleAgreementChange}
              />
              <label htmlFor="agreeToAll">전체 동의</label>
            </div>
            <div className="agreement-item">
              <input
                type="checkbox"
                id="agreeToTerms"
                name="agreeToTerms"
                checked={agreements.agreeToTerms}
                onChange={handleAgreementChange}
              />
              <label htmlFor="agreeToTerms">
                <span onClick={() => showTerms('terms')}>서비스 이용약관 (필수)</span>
              </label>
            </div>
            <div className="agreement-item">
              <input
                type="checkbox"
                id="agreeToPrivacyPolicy"
                name="agreeToPrivacyPolicy"
                checked={agreements.agreeToPrivacyPolicy}
                onChange={handleAgreementChange}
              />
              <label htmlFor="agreeToPrivacyPolicy">
                <span onClick={() => showTerms('privacy')}>개인정보처리방침 (필수)</span>
              </label>
            </div>
            <div className="agreement-item">
              <input
                type="checkbox"
                id="agreeToLocation"
                name="agreeToLocation"
                checked={agreements.agreeToLocation}
                onChange={handleAgreementChange}
              />
              <label htmlFor="agreeToLocation">
                <span onClick={() => showTerms('location')}>위치기반서비스 이용약관 (필수)</span>
              </label>
            </div>
            <div className="agreement-item">
              <input
                type="checkbox"
                id="agreeToSensitiveInfo"
                name="agreeToSensitiveInfo"
                checked={agreements.agreeToSensitiveInfo}
                onChange={handleAgreementChange}
              />
              <label htmlFor="agreeToSensitiveInfo">
                <span onClick={() => showTerms('sensitive')}>민감정보 처리 동의서 (필수)</span>
              </label>
            </div>
          </div>

          <button type="submit" className="register-button">
            회원가입하기
          </button>
        </form>
        <div className="login-link">
          <p>
            이미 계정이 있나요? <Link to="/login">로그인 하러가기</Link>
          </p>
        </div>
      </div>
      {modal.isOpen && (
        <TermsModal
          title={modal.title}
          content={modal.content}
          onClose={() => setModal({ isOpen: false, title: '', content: '' })}
        />
      )}
    </div>
  );
};

export default RegisterPage;
